import React, { useState, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PostcodeFilters from "../components/Postcodes/PostcodesFilters";
import PostcodesTable from "../components/Postcodes/PostcodesTable";
import LocationIcon from "../assets/images/location_pin_rgb_red.svg";
import PaginationComponent from "../components/PaginationComponent";
import PostcodesButtons from "../components/Postcodes/PostcodesButtons";
import "../assets/styles/postcodes.scss";
import api from "../services/httpClient";

const Postcodes = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchVersion, setFetchVersion] = useState(0);

  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);
  const lastFetchedVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;

  const [data, setData] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  React.useEffect(() => {
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, []);

  React.useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current;
    const sort = sortConfigRef.current;

    (async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/Postcode/List`, {
          params: {
            pageNumber,
            pageSize,
            SortBy: sort.key,
            SortDirection: sort.direction,
            ...filtersToApply,
          },
          signal: controller.signal,
        });

        if (cancelled) return;

        const responsePageNumber = Number(response.data.pageNumber || pageNumber);
        const responseTotalCountRaw =
          response.data.totalCount ??
          response.data.itemsCount ??
          response.data.totalRecords;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setData({
          items: Array.isArray(response.data.items) ? response.data.items : [],
          itemsCount: responseTotalCount,
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled || error?.name === "CanceledError" || error?.name === "AbortError") return;
        setData({
          items: [],
          itemsCount: null,
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize,
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => {
      const nextFilters = {
        ...prevFilters,
        [field]: value,
      };
      filtersRef.current = nextFilters;
      return nextFilters;
    });
  };

  const handleSearch = () => {
    setAppliedFilters(filtersRef.current);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handlePageChange = (page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  return (
    <div className="postcodes-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={LocationIcon}
              alt="Location Pin"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Kody pocztowe
            </h3>
          </Col>
          <Col xs="auto">
            <PostcodesButtons
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              filters={filters}
            />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <PostcodeFilters onFilterChange={handleFilterChange} />
          </Col>
        </Row>
        <Row>
          <Col>
            <PostcodesTable
              data={data.items}
              isLoading={isLoading}
              onSortChange={handleSortChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={data.pageSize}
            dataCount={data.items.length}
            itemsCount={data.itemsCount ?? rememberedTotalCount}
            totalCount={rememberedTotalCount}
            pageNumber={data.pageNumber}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default Postcodes;
